import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { tauxAPI, leadsAPI, statsAdsAPI, abonnesAnnuelsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Settings, Users, Save, RefreshCw, Megaphone, FileSearch, Mail, TrendingUp, CreditCard } from 'lucide-react';

const AdminPage = () => {
  const [taux, setTaux] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statsAds, setStatsAds] = useState(null);
  const [abonnes, setAbonnes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    taux_urssaf_bic_vente: '',
    taux_urssaf_bic_service: '',
    taux_urssaf_bnc: '',
    taux_tva_normal: '',
    taux_tva_intermediaire: '',
    taux_tva_reduit: '',
    plafond_ca_bic_vente: '',
    plafond_ca_bic_service: '',
    plafond_ca_bnc: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tauxRes, leadsRes, statsRes, abonnesRes] = await Promise.all([
        tauxAPI.get(),
        leadsAPI.getAll(),
        statsAdsAPI.get().catch(() => ({ data: null })),
        abonnesAnnuelsAPI.get().catch(() => ({ data: null }))
      ]);
      setTaux(tauxRes.data);
      setLeads(leadsRes.data);
      setStatsAds(statsRes.data);
      setAbonnes(abonnesRes.data);
      setFormData({
        taux_urssaf_bic_vente: tauxRes.data.taux_urssaf_bic_vente.toString(),
        taux_urssaf_bic_service: tauxRes.data.taux_urssaf_bic_service.toString(),
        taux_urssaf_bnc: tauxRes.data.taux_urssaf_bnc.toString(),
        taux_tva_normal: tauxRes.data.taux_tva_normal.toString(),
        taux_tva_intermediaire: tauxRes.data.taux_tva_intermediaire.toString(),
        taux_tva_reduit: tauxRes.data.taux_tva_reduit.toString(),
        plafond_ca_bic_vente: tauxRes.data.plafond_ca_bic_vente.toString(),
        plafond_ca_bic_service: tauxRes.data.plafond_ca_bic_service.toString(),
        plafond_ca_bnc: tauxRes.data.plafond_ca_bnc.toString()
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTaux = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        taux_urssaf_bic_vente: parseFloat(formData.taux_urssaf_bic_vente),
        taux_urssaf_bic_service: parseFloat(formData.taux_urssaf_bic_service),
        taux_urssaf_bnc: parseFloat(formData.taux_urssaf_bnc),
        taux_tva_normal: parseFloat(formData.taux_tva_normal),
        taux_tva_intermediaire: parseFloat(formData.taux_tva_intermediaire),
        taux_tva_reduit: parseFloat(formData.taux_tva_reduit),
        plafond_ca_bic_vente: parseFloat(formData.plafond_ca_bic_vente),
        plafond_ca_bic_service: parseFloat(formData.plafond_ca_bic_service),
        plafond_ca_bnc: parseFloat(formData.plafond_ca_bnc)
      };
      await tauxAPI.update(updateData);
      toast.success('Taux mis à jour avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-64 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Administration</h1>
          <p className="text-muted-foreground">Gérez les paramètres du Pack</p>
        </div>
        <Link to="/admin/commercial" data-testid="link-admin-commercial">
          <Button variant="outline"><TrendingUp className="h-4 w-4 mr-2" />Tableau de bord commercial</Button>
        </Link>
      </div>

      <Tabs defaultValue="stats" className="space-y-6">
        <TabsList>
          <TabsTrigger value="stats" data-testid="tab-stats-pub">
            <Megaphone className="h-4 w-4 mr-2" />
            Stats Pub
          </TabsTrigger>
          <TabsTrigger value="taux" data-testid="tab-taux">
            <Settings className="h-4 w-4 mr-2" />
            Taux & Plafonds
          </TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">
            <Users className="h-4 w-4 mr-2" />
            Leads Webinaire
          </TabsTrigger>
          <TabsTrigger value="annuels" data-testid="tab-abonnes-annuels">
            <CreditCard className="h-4 w-4 mr-2" />
            Abonnés annuels
          </TabsTrigger>
        </TabsList>

        {/* Stats Pub Tab */}
        <TabsContent value="stats">
          <div className="space-y-6" data-testid="stats-pub-content">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Analyses sur /audit-facture et emails capturés — comptage anonyme, conforme RGPD
              </p>
              <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-stats">
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <FileSearch className="h-4 w-4" /> Analyses aujourd'hui
                  </div>
                  <p className="text-3xl font-bold" data-testid="stat-audits-today">{statsAds?.audits_today ?? 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total : {statsAds?.total_audits ?? 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Mail className="h-4 w-4" /> Emails aujourd'hui
                  </div>
                  <p className="text-3xl font-bold" data-testid="stat-leads-today">{statsAds?.leads_today ?? 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total : {statsAds?.total_leads ?? 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <TrendingUp className="h-4 w-4" /> Conversion
                  </div>
                  <p className="text-3xl font-bold text-[#10B981]" data-testid="stat-conversion">{statsAds?.taux_conversion ?? 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">analyse → email</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <FileSearch className="h-4 w-4" /> Score moyen
                  </div>
                  <p className="text-3xl font-bold" data-testid="stat-score-moyen">
                    {statsAds?.score_moyen != null ? `${statsAds.score_moyen}/100` : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">conformité des factures</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base font-['Manrope']">14 derniers jours</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Factures analysées</TableHead>
                      <TableHead className="text-right">Emails laissés</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(statsAds?.per_day || []).slice().reverse().map((d) => (
                      <TableRow key={d.date}>
                        <TableCell className="font-medium">
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                        </TableCell>
                        <TableCell className="text-right">{d.audits}</TableCell>
                        <TableCell className="text-right">{d.leads}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base font-['Manrope']">Derniers emails capturés (audit facture)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Prénom</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Verdict</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(statsAds?.recent_leads || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          Aucun email capturé pour le moment
                        </TableCell>
                      </TableRow>
                    ) : (
                      statsAds.recent_leads.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="font-medium">{l.email}</TableCell>
                          <TableCell>{l.prenom || '-'}</TableCell>
                          <TableCell>{l.score_global}/100</TableCell>
                          <TableCell>
                            {l.verdict === 'conforme' && <span className="text-[#10B981] font-medium">Conforme</span>}
                            {l.verdict === 'partiel' && <span className="text-amber-500 font-medium">Partiel</span>}
                            {l.verdict === 'non_conforme' && <span className="text-red-500 font-medium">Non conforme</span>}
                          </TableCell>
                          <TableCell>{formatDate(l.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Taux Tab */}
        <TabsContent value="taux">
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center justify-between">
                <span>Taux URSSAF & TVA 2026</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadData}
                  data-testid="refresh-taux"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveTaux} className="space-y-8">
                {/* URSSAF */}
                <div>
                  <h3 className="font-semibold mb-4">Taux cotisations URSSAF (%)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>BIC Vente</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.taux_urssaf_bic_vente}
                        onChange={(e) => setFormData({ ...formData, taux_urssaf_bic_vente: e.target.value })}
                        data-testid="input-taux-bic-vente"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>BIC Services</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.taux_urssaf_bic_service}
                        onChange={(e) => setFormData({ ...formData, taux_urssaf_bic_service: e.target.value })}
                        data-testid="input-taux-bic-service"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>BNC (Libéral)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.taux_urssaf_bnc}
                        onChange={(e) => setFormData({ ...formData, taux_urssaf_bnc: e.target.value })}
                        data-testid="input-taux-bnc"
                      />
                    </div>
                  </div>
                </div>

                {/* TVA */}
                <div>
                  <h3 className="font-semibold mb-4">Taux TVA (%)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Taux normal</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.taux_tva_normal}
                        onChange={(e) => setFormData({ ...formData, taux_tva_normal: e.target.value })}
                        data-testid="input-tva-normal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Taux intermédiaire</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.taux_tva_intermediaire}
                        onChange={(e) => setFormData({ ...formData, taux_tva_intermediaire: e.target.value })}
                        data-testid="input-tva-intermediaire"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Taux réduit</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.taux_tva_reduit}
                        onChange={(e) => setFormData({ ...formData, taux_tva_reduit: e.target.value })}
                        data-testid="input-tva-reduit"
                      />
                    </div>
                  </div>
                </div>

                {/* Plafonds */}
                <div>
                  <h3 className="font-semibold mb-4">Plafonds CA (€)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>BIC Vente</Label>
                      <Input
                        type="number"
                        value={formData.plafond_ca_bic_vente}
                        onChange={(e) => setFormData({ ...formData, plafond_ca_bic_vente: e.target.value })}
                        data-testid="input-plafond-bic-vente"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>BIC Services</Label>
                      <Input
                        type="number"
                        value={formData.plafond_ca_bic_service}
                        onChange={(e) => setFormData({ ...formData, plafond_ca_bic_service: e.target.value })}
                        data-testid="input-plafond-bic-service"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>BNC (Libéral)</Label>
                      <Input
                        type="number"
                        value={formData.plafond_ca_bnc}
                        onChange={(e) => setFormData({ ...formData, plafond_ca_bnc: e.target.value })}
                        data-testid="input-plafond-bnc"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Dernière mise à jour: {taux?.updated_at ? formatDate(taux.updated_at) : 'N/A'}
                  </p>
                  <Button type="submit" disabled={saving} data-testid="save-taux-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center justify-between">
                <span>Leads Webinaire ({leads.length})</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadData}
                  data-testid="refresh-leads"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>SIRET</TableHead>
                    <TableHead>Activité</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Aucun lead enregistré
                      </TableCell>
                    </TableRow>
                  ) : (
                    leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">
                          {lead.first_name} {lead.last_name}
                        </TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>{lead.siret || '-'}</TableCell>
                        <TableCell>
                          {lead.activite === 'liberal' && 'Libéral'}
                          {lead.activite === 'artisan' && 'Artisan'}
                          {lead.activite === 'commercant' && 'Commerçant'}
                          {lead.activite === 'service' && 'Services'}
                          {!lead.activite && '-'}
                        </TableCell>
                        <TableCell>{formatDate(lead.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Abonnés annuels Tab */}
        <TabsContent value="annuels">
          <Card data-testid="abonnes-annuels-content">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Abonnés annuels — 299€/an ({abonnes?.total ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!abonnes || abonnes.abonnes.length === 0 ? (
                <p className="text-muted-foreground text-sm" data-testid="abonnes-annuels-empty">
                  Aucun abonné annuel pour le moment. Les abonnements souscrits via /achat apparaîtront ici avec leur statut PayPal en direct.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Statut PayPal</TableHead>
                      <TableHead>Début</TableHead>
                      <TableHead>Fin d&apos;accès théorique</TableHead>
                      <TableHead>Prochain prélèvement PayPal</TableHead>
                      <TableHead>Dernier paiement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {abonnes.abonnes.map((a) => (
                      <TableRow key={a.email} data-testid={`abonne-annuel-row-${a.email}`}>
                        <TableCell className="font-medium">{a.email}</TableCell>
                        <TableCell>
                          {a.paypal_status === 'ACTIVE' && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Actif</Badge>}
                          {a.paypal_status === 'CANCELLED' && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Annulé côté PayPal</Badge>}
                          {a.paypal_status === 'SUSPENDED' && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Suspendu</Badge>}
                          {!['ACTIVE', 'CANCELLED', 'SUSPENDED'].includes(a.paypal_status) && <Badge variant="secondary">{a.paypal_status || 'Inconnu'}</Badge>}
                        </TableCell>
                        <TableCell>{a.abonnement_debut ? new Date(a.abonnement_debut).toLocaleDateString('fr-FR') : '—'}</TableCell>
                        <TableCell>{a.abonnement_expire ? new Date(a.abonnement_expire).toLocaleDateString('fr-FR') : '—'}</TableCell>
                        <TableCell>{a.paypal_next_billing ? new Date(a.paypal_next_billing).toLocaleDateString('fr-FR') : '—'}</TableCell>
                        <TableCell>{a.last_payment_at ? `${new Date(a.last_payment_at).toLocaleDateString('fr-FR')}${a.last_payment_amount ? ` · ${a.last_payment_amount}€` : ''}` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
